import { IYourAppServer } from "../YourAppServer.service";
import { Subject } from "rxjs/Subject";
import { Request, UappErrorCode } from "../http.service";
import * as moment from "moment";
import { environment } from "../../../../environments/environment";
import { StorageHive, LocalKeys, UserLocalKeys } from "../valueStorage.service";
import Utils from "../../shared/utils";
import { SetTermConfirmedModel } from "../../shared/models/term_confirmed.model";
import { Observable } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { AnonymizeCustomerSuccessModel } from "../../shared/models/userInfo.model";
import { ClientType } from "../com/comEnvironmentClient";
import { L } from "@angular/core/src/render3";
import { Console } from "console";

type LoginType = "internalLogin" | "externalLogin";

/** Holder for stored LoginData on local storage */
export class StoreLoginData {

  /** Checks whether `provider` is one of the supported thirdparty login providers. */
  public static IsThirdPartyProvider(provider: string): boolean {
    return (provider === "Google" || provider === "Facebook");
  }

  public nobody: boolean = false;
  public anonymous: boolean = false;
  public registerd: boolean = false;
  public expiresUtcUnix: number = -1;
  public bearerToken: string = "";
  public refreshToken: string = "";
  public provider: string = "";
  public userName: string = "";
  public loginType: LoginType = "internalLogin";

  constructor() { }

  /** Checks, is all needed data there */
  public IsOkay(): boolean {
    let isOkay = false;
    if (this.provider !== "" &&
      (this.provider === environment.YourAppServer.ClientID || StoreLoginData.IsThirdPartyProvider(this.provider)) &&
      this.bearerToken !== "" &&
      this.refreshToken !== "" &&
      this.expiresUtcUnix !== -1 &&
      this.userName !== "") {
      isOkay = true;
    }
    return isOkay;
  }
}

/** Service to authentification over YourAPPServer */
export class YourAppServerAuth {

  private _clearUserData: Subject<void> = new Subject();

  private _Nobody: boolean = false;
  private _Anonymous: boolean = false;
  //private _Registerd: boolean = false;
  private _ExpiresUtc: moment.Moment = moment.min();
  private _BearerToken: string = "";
  /** Token to refresh the AccessToken */
  private _RefreshToken: string = "";

  // todo is there are need?
  private _Provider: string = "";
  private _UserName: string = "";
  private _LoginType: LoginType;

  constructor(public readonly YourAppServer: IYourAppServer) {
  }

  /** Authorization Info: Provider of login */
  public get Provider(): string {
    return this._Provider;
  }

  /** Authorization Info: Name for User login */
  public get UserName(): string {
    return this._UserName;
  }

  public get isLoginInternal(): boolean {
    return this._LoginType === "internalLogin";
  }

  /** Authorization token for the API */
  public get BearerToken(): string {
    return this._BearerToken;
  }

  /** time for expire the API (moment.min is, when no expire known) */
  public get ExpireUtc(): moment.Moment {
    return this._ExpiresUtc;
  }

  /** is the login a registerd user */
  //public get IsRegisterd(): boolean {
  //  return this._Registerd;
  //}

  /** is the login a anonymous user */
  public get IsAnonymous(): boolean {
    return this._Anonymous;
  }

  /** is the login a nobody (no user) */
  public get IsNobody(): boolean {
    return this._Nobody;
  }

  /** is the login a customer */
  public get IsCustomer(): boolean {
    const me = this;
    return me.IsActive && !(me.IsAnonymous || me.IsNobody);
  }

  /** check offline is the authorization over BearerToken active */
  public get IsActive(): boolean {
    // BearerToken must be set and Expires must be in the future
    return this._BearerToken !== "" && this._ExpiresUtc > moment.utc();
  }

  /** event about the clearin user data (to clear own saved user data) */
  public get ObserveClearUserData(): Observable<void> {
    return this._clearUserData;
  }

  /** Store the data from storaged login data
   * @param stored_data
   */
  private ReadTokenFromStorageData(stored_data: StoreLoginData): boolean {
    const me = this;
    let success = false;
    try {
      if (stored_data.IsOkay()) {
        me._BearerToken = stored_data.bearerToken;
        me._ExpiresUtc = moment(stored_data.expiresUtcUnix);
        me._RefreshToken = stored_data.refreshToken;

        me._UserName = stored_data.userName;
        me._Nobody = stored_data.nobody;
        me._Anonymous = stored_data.anonymous;
        me._LoginType = stored_data.loginType;
        //me._Registerd = stored_data.registerd;
        success = true;
      }
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadTokenFromStorageData", "try", error);
    }
    return success;
  }

  /** Store the data from authentification body
   * @param body HTTP Body with login data
   */
  private ReadTokenFromNobodyRequestBody(body: any): boolean {
    const me = this;
    let success = false;
    try {
      // it must be NobodySuccessLoginModel
      if (body.data_type === "NobodySuccessLoginModel") {
        me._BearerToken = body.data.access_token;
        me._ExpiresUtc = moment.utc(body.data.expires_utc);
        me._RefreshToken = body.data.refresh_token;

        me._Provider = body.data.provider;
        me._UserName = "Nobody";
        me._Nobody = true;
        success = true;
        me.SaveAuthInStorage();
      }
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadTokenFromNobodyRequestBody", "try", error);
    }
    return success;
  }

  /** Store the data from authentification body
   * @param body HTTP Body with login data
   */
  private ReadTokenFromAnonymousRequestBody(body: any): boolean {
    const me = this;
    let success = false;
    try {
      // it must be AnonymousSuccessLoginModel
      if (body.data_type === "AnonymousSuccessLoginModel") {
        me._BearerToken = body.data.access_token;
        me._ExpiresUtc = moment.utc(body.data.expires_utc);
        me._RefreshToken = body.data.refresh_token;

        me._Provider = body.data.provider;
        me._UserName = "Anonymous";
        me._Anonymous = true;
        success = true;
        me.SaveAuthInStorage();
      }
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadTokenFromAnonymousRequestBody", "try", error);
    }
    return success;
  }

  /** Store the data from authentification body
   * @param body HTTP Body with login data
   */
  private ReadTokenFromCustomerRequestBody(body: any): boolean {
    const me = this;
    let success = false;
    try {
      // it must be CustomerSuccessLoginModel
      if (body.data_type === "CustomerSuccessLoginModel") {
        me._BearerToken = body.data.access_token;
        me._ExpiresUtc = moment.utc(body.data.expires_utc);
        me._RefreshToken = body.data.refresh_token;

        me._Provider = body.data.provider;
        me._UserName = body.data.fullname;
        me._LoginType = body.data.login_type as LoginType;
        success = true;
        me.SaveAuthInStorage();
      }
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadTokenFromCustomerRequestBody", "try", error);
    }
    return success;
  }

  /** Store the data from authentification body
   * @param body HTTP Body with login data
   */
  private ReadTokenFromRequestBody(body: any): boolean {
    const me = this;
    let success = false;
    try {
      // it must be a bearer
      if (body.token_type === "bearer") {
        // body is (exemple)
        // issued_utc: "Wed, 13 Feb 2019 08:01:35 GMT"
        // expires_utc: "Wed, 27 Feb 2019 08:01:35 GMT"
        // expires_in: 1209599 // number is time in sec
        // token_type: "bearer"
        // access_token: "U5oRrSEdp7VHXvFtIKg1"
        // refresh_token: "hmckR5Zc5I8UOap5vLnQ"
        // extra params
        // user_name: "hoffmann@netcon-interactive.de"
        me._BearerToken = body.access_token;
        me._ExpiresUtc = moment.utc(body.expires_utc);
        me._RefreshToken = body.refresh_token;
        //me._Registerd = true; // userlogin can only make a registerd user

        me._Provider = body.provider;
        me._UserName = body.user_name;
        success = true;
        me.SaveAuthInStorage();
      }
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadTokenFromRequestBody", "try", error);
    }
    return success;
  }

  /** Hilfsfunktion, da der anwender sein namen nachträglich bei der regestrierung setzt
   * damit ist die Authentifikation gültig
   */
  public ReadUserNameFromUser(): void {
    const me = this;
    try {
      me.YourAppServer.User.GetUserInfo().then((UserInfo) => {
        me._UserName = UserInfo.fname;
        me.SaveAuthInStorage();
      });
    } catch (error) {
      me.YourAppServer.coreLogService.error("YourAppServerAuth.ReadUserNameFromUser", "try", error);
    }
  }

  /** Clean up all stored data */
  private ClearData(): void {
    const me = this;
    me._BearerToken = "";
    me._ExpiresUtc = moment.min();
    me._RefreshToken = "";
    me._Provider = "";
    me._UserName = "";
    me._Nobody = false;
    me._Anonymous = false;
    //me._Registerd = false;
    // free storage
    UserLocalKeys.forEach(UserLocalKey => {
      me.YourAppServer.coreCOMService.environment.valueStorage.remove(StorageHive.Local, UserLocalKey)
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerAuth.ClearData", "_valueStorageService.remove UserLocalKey: " + UserLocalKey, error); });
    });
    // is a UserData clearing needs?
    me.YourAppServer.User.ClearData()
      .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerAuth.ClearData", "User.ClearData", error); });
    me._clearUserData.next(); // alle enderen müssen ihre UserDaten ebenfalls bereinigen
  }

  /** Saving the auth to storage */
  private SaveAuthInStorage(): void {
    const me = this;
    let Data: StoreLoginData;
    if (me.IsActive) {
      Data = new StoreLoginData();
      Data.bearerToken = me._BearerToken;
      Data.refreshToken = me._RefreshToken;
      Data.expiresUtcUnix = me._ExpiresUtc.valueOf();
      Data.nobody = me._Nobody;
      Data.anonymous = me._Anonymous;
      //Data.registerd = me._Registerd;
      Data.provider = me._Provider;
      Data.userName = me._UserName;
      Data.loginType = me._LoginType;
    }
    me.YourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.yourAppAuthData, Data)
      .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerAuth.SaveAuthInStorage", "_valueStorageService.set", error); });
  }

  /** RefreshToken */
  public RefreshToken(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      Promise.reject("no internet");
    }

    if (me.IsActive) {
      return new Promise<boolean>(async (resolve, reject) => {
        // erstelle request für die re-Authorization
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: "token",
          method: "POST",
          headers: {
            // reciving data as JSON
            "Accept": "application/json",
            // sending data as urlencoded in body
            "Content-Type": "application/x-www-form-urlencoded" // muss urlencoded sein
          },
          // parameter body urlencoded übergeben
          body: me.YourAppServer.httpService.UrlencodedParams({
            grant_type: "refresh_token",
            client_id: environment.YourAppServer.ClientID,
            client_fingerprint: await me.YourAppServer.Api.GetDeviceToken(),
            access_token: me._BearerToken,
            refresh_token: me._RefreshToken
          })
        });

        // führe die aktualisierung des Token durch, ok sagt aus ob es funktioniert hat
        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok && me.ReadTokenFromRequestBody(response.body)) {
              me.YourAppServer.User.GetUserInfo(true).then(userInfo => resolve(!Utils.IsNull(userInfo))).catch(reject);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.RefreshToken", "HttpService.Request", error);
            reject(error);
          });
      });
    } else {
      // Token ist nicht mehr aktiv, ein refresch ist nicht möglich
      return Promise.resolve(false);
    }
  }

  /** CheckToken */
  public CheckToken(BearerToken: string): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      Promise.reject("no internet");
    }

    return new Promise<boolean>(async (resolve, reject) => {
      // todo eine eigene Funktion für die Prüfung bereitstellen
      // erstelle request für die re-Authorization
      const req = new Request({
        baseURI: me.YourAppServer.BaseURI,
        uri: me.YourAppServer.Api.MainPath + "like/GetNewLikes",
        method: "POST",
        headers: {
          // use Token
          "Authorization": "Bearer " + BearerToken,
          // reciving data as JSON
          "Accept": "application/json",
          // sending data as urlencoded in body
          "Content-Type": "application/x-www-form-urlencoded" // muss urlencoded sein
        },
        // Abzurufende daten sind nicht relevant, sondern ob der abruf möglich ist
        body: me.YourAppServer.httpService.UrlencodedParams({
          changes_since_ymd: 20300101,
          changes_since_hms: 0
        })
      });

      // führe die aktualisierung des Token durch, ok sagt aus ob es funktioniert hat
      me.YourAppServer.httpService.Request(req)
        .then((response) => {
          resolve(response.ok);
        }).catch((error) => {
          me.YourAppServer.coreLogService.error("YourAppServerAuth.CheckToken", "HttpService.Request", error);
          reject(error);
        });
    });
  }


  /** Wenn noch eingeloggt, wird erst ausgeloggt.
   * Der neue Login wird erst danach über die Factory gestartet.
   * @param signInFactory Funktion, die den Login-Prozess startet
   */
  private TrySignOutBeforeSignIn(signInFactory: () => Promise<boolean>): Promise<boolean> {
    const me = this;

    if (me.IsActive) {
      return new Promise<boolean>((resolve, reject) => {
        me.SignOut()
          .then((logout: boolean) => {
            if (!logout) {
              me.ClearData();
            }

            signInFactory().then(resolve).catch(reject);
          })
          .catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.TrySignOut", "me.SignOut", error);

            me.ClearData();

            signInFactory().then(resolve).catch(reject);
          });
      });
    } else {
      return signInFactory();
    }
  }

  /** Reads the sing data from local storage */
  public SingInFromStorage(): Promise<boolean> {
    const me = this;
    if (!me.IsActive) {
      return new Promise<boolean>((resolve /* no reject */) => {
        me.YourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.yourAppAuthData)
          .then(async (value) => {
            if (!Utils.IsNull(value)) {
              const storageData = Utils.GetExtend(new StoreLoginData(), value);
              // if online check stored session
              if (me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
                if (await me.CheckToken(storageData.bearerToken)) {
                  me.ReadTokenFromStorageData(storageData);
                } else {
                  // not use old session, it generates a new session if need
                }
              } else {
                // ohne internet erstmal annehmen, dass es so stimmt
                me.ReadTokenFromStorageData(storageData);
              }
            } // else inactive
            // todo is getUserData needed?
            resolve(me.IsActive);
          })
          .catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.SingInFromStorage", "valueStorage.get", error);
            resolve(false);
          });
      });
    } else {
      return Promise.resolve(false);
    }
  }

  /** Meldet den User ohne Account am YourAppServer an */
  public SignInNobody(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return me.TrySignOutBeforeSignIn(() => {
      return new Promise<boolean>((resolve, reject) => {
        // erstelle request für die Authorization
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/SignInNobody",
          method: "POST",
          headers: {
            // reciving data as JSON
            "Accept": "application/json"
          },
          // parameter body urlencoded übergeben
          body: {
            "client_id": environment.YourAppServer.ClientID
          }
        });

        // führe die anmeldung durch, ok sagt aus ob es funktioniert hat
        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok && me.ReadTokenFromNobodyRequestBody(response.body)) {
              resolve(true);
            } else {
              resolve(false);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.SignInNobody", "HttpService.Request", error);
            reject(error);
          });
      })
    });
  }

  /** Meldet den User annonym, anhand des Geräts am YourAppServer an */
  public SignInAnonymous(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return me.TrySignOutBeforeSignIn(() => {
      return new Promise<boolean>(async (resolve, reject) => {
        // erstelle request für die Authorization
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/SignInAnonymous",
          method: "POST",
          headers: {
            // reciving data as JSON
            "Accept": "application/json",
          },
          body: {
            "client_id": environment.YourAppServer.ClientID,
            "client_fingerprint": await me.YourAppServer.Api.GetDeviceToken()
          }
        });

        // führe die anmeldung durch, ok sagt aus ob es funktioniert hat
        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok && me.ReadTokenFromAnonymousRequestBody(response.body)) {
              me.YourAppServer.User.GetUserInfo(true).then(userInfo => resolve(!Utils.IsNull(userInfo))).catch(reject);
            } else {
              resolve(false);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.SignInAnonymous", "HttpService.Request", error);
            reject(error);
          });
      })
    });
  }

  /** Wenn kein login vorliegt, versuche entsprechendes Login durchzuführen */
  public SignInAnonymousOrNobodyIfNeed(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    if (!me.IsActive) {
      if (me.YourAppServer.coreCOMService.environment.client.isMobile) {
        return me.SignInAnonymous();
      } else {
        return me.SignInNobody();
      }
    }
  }

  /** versuche entsprechendes Login durchzuführen */
  public SignInAnonymousOrNobody(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    if (me.YourAppServer.coreCOMService.environment.client.isMobile) {
      return me.SignInAnonymous();
    } else {
      return me.SignInNobody();
    }
  }

  /** Meldet den User als Customer, anhand der Mail und Passwords am YourAppServer an
  * @param mail E-Mailadresse vom Benutzer
  * @param password Kennwort des Benutzers
  */
  public SignInCustomer(mail: string, password: string): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return me.TrySignOutBeforeSignIn(() => {
      return new Promise<boolean>(async (resolve, reject) => {
        // erstelle request für die Authorization
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/SignInCustomer",
          method: "POST",
          headers: {
            // reciving data as JSON
            "Accept": "application/json",
          },
          // parameter body urlencoded übergeben
          body: {
            "client_id": environment.YourAppServer.ClientID,
            "client_fingerprint": await me.YourAppServer.Api.GetDeviceToken(),
            "mail": mail,
            "password": password
          }
        });

        // führe die anmeldung durch, ok sagt aus ob es funktioniert hat
        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok) {
              if (me.ReadTokenFromCustomerRequestBody(response.body)) {
                me.YourAppServer.User.GetUserInfo(true).then(userInfo => resolve(!Utils.IsNull(userInfo))).catch(reject);
              } else {
                reject("parse request error");
              }
            } else {
              if (response.ya_error_code === UappErrorCode.NOT_FOUND) {
                resolve(false); // no user found for mail and password
              }
              // direckte rückgabe des fehlercodes
              reject(response.ya_error_code);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.SignInCustomer", "HttpService.Request", error);
            reject(error);
          });
      })
    });
  }

  /** Email zum verefizieren der eMail wird versendet
  * @param mail E-Mailadresse vom Benutzer
  * @param password Kennwort des Benutzers
  * @param terms  list of [id: int, version_ymd: int, version_ymd: int]
  */
  public RegisterCustomer(mail: string, password: string, terms: SetTermConfirmedModel[]): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return new Promise<boolean>(async (resolve, reject) => {
      // prepare Register Request
      const req = new Request({
        baseURI: me.YourAppServer.BaseURI,
        uri: me.YourAppServer.Api.MainPath + "account/RegisterCustomer",
        method: "POST",
        headers: {}, // set the authorization token later if has
        body: {
          "language": InternationalisationService.locale,
          "client_id": environment.YourAppServer.ClientID,
          "client_fingerprint": await me.YourAppServer.Api.GetDeviceToken(),
          "mail": mail,
          "password": password,
          "terms": terms
        }
      });
      // set the authorization token, if he is authorized anonymous (um den Account zu übernehmen)
      if (me.IsActive && me.IsAnonymous) {
        req.headers["Authorization"] = "Bearer " + me.BearerToken;
      }

      // führe die anfrage zum mail bestätigen durch, ok sagt aus ob es funktioniert hat
      me.YourAppServer.httpService.Request(req)
        .then((response) => {
          if (response.ok) {
            resolve(response.body.data.success);
          } else {
            reject(response.ya_error_code); // direckte rückgabe des fehlercodes
          }
        }).catch((error) => {
          me.YourAppServer.coreLogService.error("YourAppServerAuth.RegisterCustomer", "HttpService.Request", error);
          reject(error);
        });
    });
  }

  /**
   * Registrierung mit Google-ID-Token
   * @param idToken vom Google Login
   * @param terms AGB-Zustimmungen
   */
  public SignInCustomerWithGoogle(idToken: string, clientID: string, terms: SetTermConfirmedModel[]): Promise<boolean> {
    const me = this;

    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return me.TrySignOutBeforeSignIn(() => {
      return new Promise<boolean>(async (resolve, reject) => {
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/ExternalLogin",
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          body: {
            client_id: environment.YourAppServer.ClientID,
            client_fingerprint: await me.YourAppServer.Api.GetDeviceToken(),
            provider: "google",
            external_access_token: idToken,
          }
        });

        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok) {
              if (me.ReadTokenFromCustomerRequestBody(response.body)) {
                me.YourAppServer.User.GetUserInfo(true).then(userInfo => resolve(!Utils.IsNull(userInfo))).catch(reject);
              } else {
                reject("parse request error");
              }
            } else {
              reject(response.ya_error_code);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.ExternalLogin", "HttpService.Request", error);
            reject(error);
          });
      })
    });
  }

  public StartGoogleOAuthRedirectFlow(): Promise<void> {
    var me = this;

    return new Promise<void>(async function (resolve, reject) {
      try {
        var deviceToken = await me.YourAppServer.Api.GetDeviceToken();

        var req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/thirdparty/signin_start",
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          body: {
            device: me.getDeviceString(),
            client_id: environment.YourAppServer.ClientID,
            client_fingerprint: deviceToken,
          }
        });

        me.YourAppServer.httpService.Request(req)
          .then(function (resp) {
            if (!resp || !resp.ok) {
              reject("signin_start failed");
              return;
            }

            var body: any = resp.body;

            // falls Serializer String liefert
            if (typeof body === "string") {
              try { body = JSON.parse(body); } catch (e) { }
            }

            var actionUrl = null;

            if (body && body.data) {
              actionUrl = body.data.ActionUrl || body.data.actionUrl;
            }

            if (!actionUrl && body) {
              actionUrl = body.ActionUrl || body.actionUrl;
            }

            if (!actionUrl) {
              console.error("signin_start: actionUrl missing", body);
              reject("signin_start: actionUrl missing");
              return;
            }

            // System-Browser redirect zu Google
            window.open(actionUrl, "_system")

            resolve();
          })
          .catch(function (e) {
            console.error("signin_start http error", e);
            reject(e);
          });

      } catch (e) {
        console.error("signin_start exception", e);
        reject(e);
      }
    });
  }

  private getDeviceString(): string {
    switch (this.YourAppServer.coreCOMService.environment.client.type) {
      case ClientType.android:
        return "android";

      case ClientType.ios:
        return "ios";

      default:
        return "browser";
    }
  }

  public GetThirdpartySigninResult(token: string): Promise<any> {
    const me = this;

    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return new Promise<any>((resolve, reject) => {
      const req = new Request({
        baseURI: me.YourAppServer.BaseURI,
        uri: me.YourAppServer.Api.MainPath + "account/thirdparty/signin_result",
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: {
          token: token
        }
      });

      me.YourAppServer.httpService.Request(req)
        .then((response) => {
          if (response.ok) {
            resolve(response.body);
          } else {
            reject(response.ya_error_code);
          }
        })
        .catch((error) => {
          me.YourAppServer.coreLogService.error("YourAppServerAuth.GetThirdpartySigninResult", "HttpService.Request", error);
          reject(error);
        });
    });
  }

  public SignInWithStoredSigninResponse(body: any): Promise<boolean> {
    const me = this;

    return me.TrySignOutBeforeSignIn(async () => {
      me.YourAppServer.coreLogService.info("YourAppServerAuth.SignInWithStoredSigninResponse", "start signInFactory");

      if (!me.ReadTokenFromCustomerRequestBody(body)) {
        throw "parse request error";
      }

      me._Nobody = false;

      me.YourAppServer.coreLogService.info("YourAppServerAuth.SignInWithStoredSigninResponse", "before GetUserInfo");

      const userInfo = await me.YourAppServer.User.GetUserInfo(true);

      me.YourAppServer.coreLogService.info("YourAppServerAuth.SignInWithStoredSigninResponse", "after GetUserInfo", userInfo);

      return !Utils.IsNull(userInfo);
    });
  }

  /**
 * Registrierung mit Google-ID-Token
 * @param accessToken vom Google Login
 * @param terms AGB-Zustimmungen
 */
  public SignInCustomerWithFacebook(accessToken: string, terms: SetTermConfirmedModel[]): Promise<boolean> {
    const me = this;

    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return me.TrySignOutBeforeSignIn(() => {
      return new Promise<boolean>(async (resolve, reject) => {
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/ExternalLogin",
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          body: {
            client_id: environment.YourAppServer.ClientID,
            client_fingerprint: await me.YourAppServer.Api.GetDeviceToken(),
            provider: "facebook",
            external_access_token: accessToken,
          }
        });

        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            if (response.ok) {
              if (me.ReadTokenFromCustomerRequestBody(response.body)) {
                me.YourAppServer.User.GetUserInfo(true).then(userInfo => resolve(!Utils.IsNull(userInfo))).catch(reject);
              } else {
                reject("parse request error");
              }
            } else {
              reject(response.ya_error_code);
            }
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.ExternalLogin", "HttpService.Request", error);
            reject(error);
          });
      })
    });
  }

  /** Benutzer wird augeloggt und die offene Session geschlossen */
  public SignOut(): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      me.ClearData();
      return Promise.reject("no internet");
    }

    return new Promise<boolean>((resolve, reject) => {
      // wenn Token inaktiv ist, ist die Abmeldung Okay
      if (me.IsActive) {
        // prepare logout Request
        const req = new Request({
          baseURI: me.YourAppServer.BaseURI,
          uri: me.YourAppServer.Api.MainPath + "account/logout",
          method: "POST",
          headers: {
            // set the authorization token
            "Authorization": "Bearer " + me.BearerToken
          },
          body: null // no body for Logout
        });
        // führe die abmeldung durch, ok sagt aus ob es funktioniert hat
        me.YourAppServer.httpService.Request(req)
          .then((response) => {
            me.ClearData();
            resolve(response.ok);
          }).catch((error) => {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.SignOut", "HttpService.Request", error);
            me.ClearData();
            reject(error);
          });
      } else {
        // wenn kein Token da ist, ist die Abmeldung Okay
        resolve(true);
      }
    });
  }

  /** Benutzer wird anonymisiert */
  public AnonymizeCustomer(): Promise<AnonymizeCustomerSuccessModel> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return new Promise<AnonymizeCustomerSuccessModel>((resolve, reject) => {
      const FunctionName = "account/AnonymizeCustomer";

      me.YourAppServer.Api.RequestService(AnonymizeCustomerSuccessModel, FunctionName, {})
        .then((response) => {
          if (response.OK) {
            me.ClearData();
            resolve(response.Data);
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.AnonymizeCustomer", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerAuth.AnonymizeCustomer", "RequestService", error); reject(); });
    });
  }

  /**
   * Change newsletter opt-in status
   * @param allowNewsletter Whether to allow newsletter
   * @returns Promise<boolean> true on success
   */
  public ChangeAllowMailNewsletter(allowNewsletter: boolean): Promise<boolean> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
      return Promise.reject("no internet");
    }

    return new Promise<boolean>(async (resolve, reject) => {
      me.YourAppServer.coreLogService.debug("YourAppServerAuth.ChangeAllowMailNewsletter", "Sending newsletter opt-in", { allowNewsletter });

      const req = new Request({
        baseURI: me.YourAppServer.BaseURI,
        uri: me.YourAppServer.Api.MainPath + "account/ChangeAllowMailNewsletter",
        method: "POST",
        headers: {
          "Authorization": "Bearer " + me.BearerToken
        },
        body: {
          AllowMailNewsletter: allowNewsletter
        }
      });

      me.YourAppServer.httpService.Request(req)
        .then((response) => {
          if (response.ok) {
            resolve(true);
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerAuth.ChangeAllowMailNewsletter", "HttpService.Request (response not ok)", response);
            reject(response.ya_error_code);
          }
        })
        .catch((error) => {
          me.YourAppServer.coreLogService.error("YourAppServerAuth.ChangeAllowMailNewsletter", "HttpService.Request", error);
          reject(error);
        });
    });
  }
}
